RivalIQ.apps.Overview.module('widgets', function (widgets, App, Backbone, Marionette, $, _) {

    'use strict';

    const LANDSCAPE_COMPARE_METRIC_LOADS = {
        PrimaryMetric: 'PrimaryMetric',
        PrimaryMetricBreakdown: 'PrimaryMetricBreakdown',
        PrimaryMetricDeltaRel: 'PrimaryMetricDeltaRel',
        PrimaryMetricBreakdownDeltaRel: 'PrimaryMetricBreakdownDeltaRel',

        BenchmarkMetric: 'BenchmarkMetric',
        BenchmarkMetricBreakdown: 'BenchmarkMetricBreakdown',
        BenchmarkMetricDeltaRel: 'BenchmarkMetricDeltaRel',
        BenchmarkMetricBreakdownDeltaRel: 'BenchmarkMetricBreakdownDeltaRel',

        ObservationMetrics: 'ObservationMetrics'
    };

    const MetricTooltip = Marionette.ItemView.extend({
        template: '#landscapeComparisonMetricTooltipTemplate',

        serializeData: function () {
            const me = this;

            const title = me.getOption('title') || me.model.getName();

            let deltaRel = me.getOption('deltaRel');
            let deltaRelClass = null;
            if (_.isFinite(deltaRel)) {
                deltaRelClass = getDeltaRelClass(deltaRel);
                deltaRel = RivalIQ.formatter.formats.percent(deltaRel);
            } else {
                deltaRel = null;
            }

            return {
                metric: me.model,
                title: title,
                metricClassName: `landscape-comparison-tooltip-legend-item ${me.getOption('metricClassName') || ''}`,
                legendClass: me.getOption('legendClass') || '',
                submetric: me.getOption('submetric') || null,
                deltaRel: deltaRel,
                deltaRelClass: deltaRelClass || ''
            };
        }
    });

    const BarTooltip = Marionette.ItemView.extend({
        template: '#landscapeComparisonBarTooltipTemplate',

        serializeData: function () {
            const me = this;

            return {
                metric: me.model,
                submetrics: me.getOption('submetrics') || [],
                metricClassName: `landscape-comparison-bar-tooltip ${me.getOption('metricClassName') || ''}`
            };
        }
    });

    const benchmarkIncompleteDataTitleTemplate = _.template($('#landscapeComparisonPartialDataTemplate').html());

    // We never want to show submetrics (submetric - if property is engagement total, then submetrics are likes, comments, shares) if we're
    // showing the median of more than one company. If it's not a median or just one company, then it's ok to show.
    function shouldShowBenchmarkSubmetrics(aggregate, benchmarkCompaniesFilter, dashboardModel) {
        if (aggregate === 'median') {
            const companies = App.utils.getCompaniesForFilter(benchmarkCompaniesFilter, dashboardModel);
            return _.size(companies) === 1;
        }

        return true;
    }

    function getDeltaRelClass(value) {
        if (_.isArray(value)) {
            value = _.head(value);
        }

        if (_.isFinite(value)) {
            if (value >= 0) {
                return 'delta-rel-positive';
            } else if (value < 0) {
                return 'delta-rel-negative';
            }
        }
    }

    // return all the possible dimension values for this metric in a consistent order.
    function getDimensionValues(metric) {
        const freeDimension = _.head(metric.getFreeDimensions());
        return metric.getStaticDimensionValues(freeDimension);
    }

    function formatForTooltip(metric, value) {
        value = (_.isArray(value) ? _.last(value) : value);
        if (!_.isFinite(value)) {
            return '--';
        }

        return metric.format(value, {
            expand: true
        });
    }

    function setupObservationPopover(domEl, content) {

        const popoverOptions = {
            content: content,
            html: true,
            container: domEl,
            placement: 'bottom',
            trigger: 'hover'
        };

        domEl.popover(popoverOptions);

        domEl.on('shown.bs.popover', function () {
            domEl.find('.popover').on('click', function (e) {
                // swallow the click event, unless the user clicked on the link in the popover.
                if (!$(e.target).is('a')) {
                    e.preventDefault();
                }
            });
        });
    }

    const LandscapeComparisonView = RivalIQ.views.LayoutView.mixin({
        className: 'overview-landscape-comparison',
        template: '#landscapeComparisonTemplate',
        mixins: {
            props: Marionette.Dashboard.mixins.Props,
            companySets: RivalIQ.apps.Shell.mixins.CompanySetEventsMixin
        },

        regions: {
            graph: '.x-dual-graph'
        },

        ui: {
            viewAllBtn: '.x-view-all',
            info: '.fa-info-circle:not(.x-observation-info)'
        },

        events: {
            'click @ui.viewAllBtn': 'showModal'
        },

        companySetEvents: {
            'change:name': 'onCompanySetNameChange'
        },

        onCompanySetNameChange: function (updatedCompanySet) {
            const me = this;

            if (updatedCompanySet.get('companySetId') === me.getProp('companySetDescriptor')) {
                // The user renamed the selected company set, re-render the view (since we display that name below the bar chart)
                me.render();
            }
        },

        onRender: function () {
            const me = this;
            me.ui.info.popover({
                trigger: 'hover',
                container: 'body',
                placement: 'auto right',
                html: true
            });

            _.each(me.model.get('observations'), function (observation, observationIdx) {
                if (observation.observationTooltip) {
                    setupObservationPopover(me.$el.find(`.observation-${observationIdx} .x-observation-info`), observation.observationTooltip);
                }
            });
        },

        getTooltipContent: function (metric, value, overrides) {
            const formattedValue = formatForTooltip(metric, value);

            const viewOptions = _.extend({
                model: new RivalIQ.models.MetricDefinition(_.extend(metric.toJSON(), {
                    value: formattedValue
                }))
            }, overrides);

            return new MetricTooltip(viewOptions).render().el.outerHTML;
        },

        getBarTooltipContent: function (metric, value, deltaRelativeByDimension, className) {
            const me = this;

            const rawValues = metric.getValue({
                format: false
            });
            let submetrics;
            const showBarParts = _.isArray(rawValues) && shouldShowBenchmarkSubmetrics(metric.get('aggregate'), metric.get('benchmarkCompaniesFilter'), me);
            if (showBarParts) {
                const dimensionValues = getDimensionValues(metric);

                const dimension = _.head(metric.getFreeDimensions());
                submetrics = _.map(dimensionValues, function (dimensionValue, idx) {
                    const submetricValue = _.find(rawValues, function (part) {
                        return part[0] === dimensionValue;
                    });

                    let deltaRel = _.get(deltaRelativeByDimension, [dimensionValue]);
                    let deltaRelClass = null;
                    if (_.isFinite(deltaRel)) {
                        deltaRelClass = getDeltaRelClass(deltaRel);
                        deltaRel = RivalIQ.formatter.percent(deltaRel);
                    } else {
                        deltaRel = '--';
                    }

                    return {
                        title: metric.formatDimension(dimension, dimensionValue),
                        value: formatForTooltip(metric, submetricValue),
                        legendClass: `x-${dimensionValue} legend-item-${idx + 1}`,
                        deltaRel: deltaRel,
                        deltaRelClass: deltaRelClass || ''
                    };
                });
            }

            const viewOptions = _.extend({
                model: new RivalIQ.models.MetricDefinition(_.extend(metric.toJSON(), {
                    value: formatForTooltip(metric, value)
                })),
                submetrics: submetrics,
                metricClassName: className
            });

            return new BarTooltip(viewOptions).render().el.outerHTML;

        },

        getBarConfig: function (metric, metricBreakdown, metricBreakdownDeltaRel, config) {
            const me = this;

            const overallValue = _.get(metric.getValue({
                format: false
            }), [0, 0]);

            let deltaRelativeByDimension = {};
            if (metricBreakdownDeltaRel) {
                const deltaRel = metricBreakdownDeltaRel.getValue({
                    format: false
                });
                deltaRelativeByDimension = _(deltaRel)
                    .keyBy(_.head)
                    .mapValues(_.last)
                    .value();
            }

            if (!config.dataNotAvailable) {
                config.tooltip = {
                    title: config.title,
                    content: me.getBarTooltipContent(metricBreakdown, overallValue, deltaRelativeByDimension, config.class)
                };
            }

            // Don't show subparts for medians as they might not sum up to the total properly.
            const breakdownValues = metricBreakdown.getValue({
                format: false
            });

            const showBarParts = _.isArray(breakdownValues) && shouldShowBenchmarkSubmetrics(metric.get('aggregate'), metric.get('benchmarkCompaniesFilter'), me);
            // using getDimensionValues instead of the order from the values so we get a consistent ordering.
            const dimensionValues = getDimensionValues(metricBreakdown);
            const dimension = _.head(metricBreakdown.getFreeDimensions());

            let values;
            if (showBarParts && !_.isEmpty(dimensionValues)) {
                values = _.map(dimensionValues, function (dimensionValue, idx) {

                    const v = _.find(breakdownValues, function (part) {
                        return part[0] === dimensionValue;
                    });

                    const barPartConfig = {
                        value: _.last(v) || 0,
                        class: `x-${dimensionValue}`
                    };

                    const pctOfTotal = RivalIQ.formatter.absolutePercent(barPartConfig.value / overallValue);

                    barPartConfig.tooltip = {
                        title: config.title,
                        content: me.getTooltipContent(metricBreakdown, barPartConfig.value, {
                            title: metricBreakdown.formatDimension(dimension, dimensionValue),
                            legendClass: `${barPartConfig.class} legend-item-${idx + 1}`,
                            metricClassName: config.class || '',
                            submetric: `(${pctOfTotal} of total)`,
                            deltaRel: _.get(deltaRelativeByDimension, [dimensionValue])
                        })
                    };

                    return barPartConfig;
                });

            } else {
                const deltaRelValue = _.get(metricBreakdownDeltaRel.getValue({
                    format: false
                }), [0, 0]);

                values = [{
                    value: overallValue,
                    tooltip: {
                        title: config.title,
                        content: me.getTooltipContent(metricBreakdown, overallValue, {
                            metricClassName: config.class || '',
                            deltaRel: deltaRelValue
                        })
                    }
                }];
            }

            return _.extend({
                primaryLabel: overallValue,
                values: values
            }, config);
        },

        makeBar1Config: function () {
            const me = this;

            const primaryMetric = me.model.get('primaryMetric');
            const primaryMetricBreakdown = me.model.get('primaryMetricBreakdown');
            const primaryMetricDeltaRel = me.model.get('primaryMetricDeltaRel');
            const primaryMetricBreakdownDeltaRel = me.model.get('primaryMetricBreakdownDeltaRel');

            const dataNotAvailable = _.isNull(_.get(primaryMetric.getValue({
                format: false
            }), [0, 0]));

            const deltaRelRaw = _.get(primaryMetricDeltaRel.getValue({
                format: false
            }), [0, 0]);

            const deltaRelClass = getDeltaRelClass(deltaRelRaw);

            const focusCompany = RivalIQ.state.landscape.getCompanyById(me.getProp('focusCompanyId'));
            const bar1Config = me.getBarConfig(primaryMetric, primaryMetricBreakdown, primaryMetricBreakdownDeltaRel, {
                title: focusCompany.get('name'),
                primaryLabelFormatter: function (value) {
                    return primaryMetric.format(value);
                },
                secondaryLabel: [ (_.isFinite(deltaRelRaw) ? RivalIQ.formatter.percent(deltaRelRaw) : '--') ],
                class: `focus-company ${deltaRelClass} ${primaryMetricBreakdown.getFreeDimensions().join(' ')}`,
                dataNotAvailable: dataNotAvailable
            });

            if (dataNotAvailable) {

                let incompleteDataText;
                const source = primaryMetric.get('source');
                const noHistoricData = primaryMetric.getNoHistoricData();
                if (RivalIQ.utils.companyDisplay.hasChannel(primaryMetric.get('channel'), focusCompany.attributes)) {
                    const dataStatus = RivalIQ.apps.Shell.utils.getDataFreshnessState();
                    const subMetrics = _.map(getDimensionValues(primaryMetricBreakdown), function (dimensionValue) {
                        return {
                            metric: dimensionValue
                        };
                    });

                    const earliestDataDateByCompanyId = primaryMetric.getEarliestDataDate();
                    const incompleteDataType = RivalIQ.utils.incompleteData.getIncompleteDataType(focusCompany, source, dataStatus.updateInFlight, primaryMetric.get('timePeriod'), earliestDataDateByCompanyId, noHistoricData, subMetrics);
                    incompleteDataText = RivalIQ.utils.incompleteData.getIncompleteDataText(incompleteDataType, focusCompany.attributes, primaryMetric.get('timePeriod'), focusCompany.get('name'), primaryMetric.getName(), source, earliestDataDateByCompanyId, null, noHistoricData, subMetrics);

                    bar1Config.notAvailableMessage = incompleteDataText.message;
                    bar1Config.notAvailableTooltip = {
                        content: RivalIQ.utils.incompleteData.incompleteDataPopoverTemplate({
                            title: incompleteDataText.title,
                            description: incompleteDataText.description
                        })
                    };
                } else {
                    const sourceDetails = RivalIQ.enums.sourceDetails[source];
                    const earliestDataDateByCompanyId = primaryMetric.getEarliestDataDate();
                    incompleteDataText = RivalIQ.utils.incompleteData.getIncompleteDataText(RivalIQ.enums.incompleteDataType.NO_ACTIVE_RECORDS, focusCompany.attributes, primaryMetric.get('timePeriod'), focusCompany.get('name'), primaryMetric.getName(), source, earliestDataDateByCompanyId, null, noHistoricData, [{
                        metric: sourceDetails.channel
                    }]);

                    bar1Config.notAvailableMessage = incompleteDataText.message;
                    bar1Config.notAvailableTooltip = {
                        content: incompleteDataText.title
                    };
                }
            } else {
                const primaryMetricCalculationMetadata = primaryMetric.getCalculationMetadata(primaryMetric.get('companyId'));
                if (!_.isEmpty(_.get(primaryMetricCalculationMetadata, ['missingSources']))) {
                    // The main period has partial data.
                    bar1Config.hasPartialData = true;
                    if (primaryMetricCalculationMetadata.missingSources.length === 1) {
                        bar1Config.partialDataMessage = `${RivalIQ.enums.sourceDetails[primaryMetricCalculationMetadata.missingSources[0]].name} data not available.`;
                    } else {
                        bar1Config.partialDataMessage = `${primaryMetricCalculationMetadata.missingSources.length} channels do not have data avilable.`;
                    }
                    const incompleteDataDetails = RivalIQ.utils.incompleteData.getIncompleteDataPopover('companyId', primaryMetric.get('companyId'), primaryMetric, primaryMetricCalculationMetadata);
                    bar1Config.partialDataTooltip = incompleteDataDetails && incompleteDataDetails.popover;
                }
            }

            return new Backbone.Model(bar1Config);
        },

        getBenchmarkMetricName: function () {
            const me = this;

            const benchmarkMetric = me.model.get('benchmarkMetric');

            const companies = App.utils.getCompaniesForFilter(benchmarkMetric.get('benchmarkCompaniesFilter'), me);
            const companySetDescriptor = me.getProp('companySetDescriptor');
            let name = App.utils.getSelectedBenchmarkCompareAgainst().name;

            if (_.size(companies) === 1 && !RivalIQ.state.utils.isOnlyCompanySet(companySetDescriptor)) {
                return companies[0].get('name');
            }

            if (companySetDescriptor) {
                const companySetName = RivalIQ.state.utils.getCompanySetName(companySetDescriptor);
                name = `${name}: ${companySetName}`;
            }

            return name;
        },

        makeBar2Config: function () {
            const me = this;

            const benchmarkMetric = me.model.get('benchmarkMetric');
            delete benchmarkMetric.attributes.format;
            const benchmarkMetricBreakdown = me.model.get('benchmarkMetricBreakdown');
            const benchmarkMetricBreakdownDeltaRel = me.model.get('benchmarkMetricBreakdownDeltaRel');
            const benchmarkMetricDeltaRel = me.model.get('benchmarkMetricDeltaRel');

            const deltaRelRaw = _.get(benchmarkMetricDeltaRel.getValue({
                format: false
            }), [0, 0]);

            const deltaRelClass = getDeltaRelClass(deltaRelRaw);

            const compareAgainst = App.utils.getSelectedBenchmarkCompareAgainst();
            const calculationMetadata = benchmarkMetric.getMetadata().calculationMetadata;
            const incompleteDataDetails = RivalIQ.utils.incompleteData.getBenchmarkIncompleteDataDetails(calculationMetadata, benchmarkMetric, compareAgainst);

            const bar2Config = me.getBarConfig(benchmarkMetric, benchmarkMetricBreakdown, benchmarkMetricBreakdownDeltaRel, {
                title: me.getBenchmarkMetricName(),
                primaryLabelFormatter: function (value) {
                    return benchmarkMetric.format(value);
                },
                secondaryLabel: [ (_.isFinite(deltaRelRaw) ? RivalIQ.formatter.percent(deltaRelRaw) : '--') ],
                class: `benchmark ${deltaRelClass} ${benchmarkMetricBreakdown.getFreeDimensions().join(' ')}`,
                dataNotAvailable: incompleteDataDetails.dataNotAvailable
            });

            if (incompleteDataDetails.hasPartialData) {
                bar2Config.hasPartialData = true;
                bar2Config.partialDataMessage = incompleteDataDetails.partialDataMessage;
                bar2Config.partialDataTooltip = incompleteDataDetails.partialDataTooltip;
            } else if (!incompleteDataDetails.dataNotAvailable && incompleteDataDetails.numMissingCompanies > 0) {
                bar2Config.titleHtml = benchmarkIncompleteDataTitleTemplate({
                    benchmarkName: me.getBenchmarkMetricName(),
                    partialDataWarning: incompleteDataDetails.partialDataMessage
                });
                bar2Config.titleTooltip = incompleteDataDetails.partialDataTooltip;
            } else if (compareAgainst.specificMetricDescription) {
                const companies = App.utils.getCompaniesForFilter(benchmarkMetric.get('benchmarkCompaniesFilter'), me);
                if (companies.length > 1) {
                    bar2Config.titleTooltip = {
                        content: compareAgainst.specificMetricDescription
                    };
                }
            }

            if (incompleteDataDetails.dataNotAvailable) {
                bar2Config.notAvailableMessage = incompleteDataDetails.dataNotAvailableMessage;
                bar2Config.notAvailableTooltip = incompleteDataDetails.dataNotAvailableTooltip;
            } else if (compareAgainst.benchmarkCompaniesFilter === RivalIQ.enums.benchmarkCompaniesFilters.all_companies.id && calculationMetadata.period2NumCompanies === 1) {
                bar2Config.notAvailableMessage = 'No competitors selected';
                bar2Config.dataNotAvailable = true;
            }

            return new Backbone.Model(bar2Config);
        },

        onAttachLoaded: function () {
            const me = this;

            const dualGraphModelConfig = {
                bars: [me.makeBar1Config(), me.makeBar2Config()]
            };

            const primaryMetricBreakdown = me.model.get('primaryMetricBreakdown');
            if (!_.isEmpty(primaryMetricBreakdown.getFreeDimensions())) {
                const dimension = _.head(primaryMetricBreakdown.getFreeDimensions());
                dualGraphModelConfig.legend = _.map(getDimensionValues(primaryMetricBreakdown), function (dimensionValue) {
                    let legendItemClassName = '';
                    const isExcluded = _.includes(primaryMetricBreakdown.get('exclusions'), dimensionValue);
                    if (isExcluded) {
                        legendItemClassName = 'excluded';
                    }

                    return {
                        id: dimensionValue,
                        name: primaryMetricBreakdown.formatDimension(dimension, dimensionValue),
                        class: `x-${dimensionValue}`,
                        legendItemClassName: legendItemClassName
                    };
                });

                dualGraphModelConfig.legendClass = dimension;
            }

            me.dualGraph = new RivalIQ.views.DualGraph({
                model: new Backbone.Model(dualGraphModelConfig)
            });
            me.listenTo(me.dualGraph, 'legend:clicked', function (legendId) {
                me.onLegendClick(legendId);
            });
            me.listenTo(me.dualGraph, 'bars:clicked', function (legendId) {
                me.showModal(legendId);
            });
            me.graph.show(me.dualGraph);
        },

        onLegendClick: function (legendId) {
            const me = this;

            let exclusions = me.getProp('exclusions') || [];
            if (_.includes(exclusions, legendId)) {
                exclusions = _.without(exclusions, legendId);
            } else {
                exclusions = _.union(exclusions, [legendId]);
            }

            const primaryMetricBreakdown = me.model.get('primaryMetricBreakdown');
            const dimensionValue = getDimensionValues(primaryMetricBreakdown);
            const freeDimension = _.head(primaryMetricBreakdown.getFreeDimensions());

            if (exclusions.length !== dimensionValue.length) {
                me.setProps({
                    exclusions: exclusions
                });

                RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.GraphLegendClicked, {
                    channel: me.getProp('channel'),
                    freeDimension: freeDimension,
                    chartType: 'landscapeComparison'
                });
            }
        },

        showModal: function () {
            const me = this;

            let metric = me.model.get('primaryMetric');

            const baseMetric = me.getProp('metrics')[me.getOption('index')] || {};
            const dimensions = _.compact(_.union(metric.dimensions, ['companyId', 'channel'], [baseMetric.exclusionDimension]));

            metric = RivalIQ.metric.build({
                timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD,
                dimensions: dimensions
            }, _.pick(metric.attributes, 'property', 'dimensions', 'channel'));

            const dashboardModel = new RivalIQ.apps.Dashboard.widgets.MetricBarChangeChartWidget(me.dashboardModel, {
                props: _.create(me.dashboardModel.attributes, {
                    inclusions: 3,
                    metrics: [
                        metric
                    ],
                    showBenchmarkLine: true,
                    renderBarPatterns: Boolean(me.getProp('channel')) // we don't want bar patterns in cross-channel
                })
            });

            const modal = new RivalIQ.apps.Analyze.views.GraphModal({
                dashboardModel: dashboardModel
            });

            RivalIQ.execute('show:modal', modal);

            RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.OverviewViewedModal, {
                primaryMetric: metric
            });
        },

        serializeData: function () {
            const me = this;

            const primaryMetric = me.model.get('primaryMetric');
            return {
                metricName: primaryMetric.getName(),
                description: primaryMetric.getDescription(),
                observations: me.model.get('observations')
            };
        }
    });

    const LandscapeComparisonContainerView = RivalIQ.views.CompositeView.mixin({
        className: function () {
            const me = this;

            const metrics = me.getProp('metrics');

            const numMetrics = _.size(metrics);

            const hasFreeDimension = _.some(metrics, 'exclusionDimension');

            return `panel-body landscape-overview-container landscape-overview-container-${numMetrics}${hasFreeDimension ? ' has-legend' : ''}`;
        },
        template: '#landscapeComparisonContainerTemplate',
        childView: LandscapeComparisonView,

        childViewContainer: '.x-landscape-container',

        mixins: {
            props: Marionette.Dashboard.mixins.Props,
            LoadDataWithBuffering: RivalIQ.apps.Dashboard.mixins.LoadDataWithBuffering,
            companySetChangeReload: RivalIQ.apps.Shell.mixins.CompanySetChangeReload
        },

        dashboardModelEvents: {
            'change': 'reload'
        },

        childViewOptions: function (model, index) {
            const me = this;

            return {
                index: index,

                dashboardModel: me.dashboardModel
            };
        },

        _loadData: function () {
            const me = this;

            const props = me.getProps();

            // Delete the format otherwise all the aggregate metrics will have the export format as their value on export.
            delete props.format;

            const selectedBenchmarkCompareAgainst = App.utils.getSelectedBenchmarkCompareAgainst();

            const metricLoadTypes = _.keys(LANDSCAPE_COMPARE_METRIC_LOADS);

            const metricCollections = _.reduce(props.metrics, function (memo, metric) {

                const primaryMetric = _.defaults({
                    dimensions: _.union(metric.dimensions, ['companyId']),
                    companyId: props.focusCompanyId,
                    timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD
                }, props, metric);
                memo[LANDSCAPE_COMPARE_METRIC_LOADS.PrimaryMetric] = (memo[LANDSCAPE_COMPARE_METRIC_LOADS.PrimaryMetric] || []).concat([primaryMetric]);

                memo[LANDSCAPE_COMPARE_METRIC_LOADS.PrimaryMetricDeltaRel] = (memo[LANDSCAPE_COMPARE_METRIC_LOADS.PrimaryMetricDeltaRel] || []).concat([_.defaults({
                    timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE
                }, primaryMetric)]);

                const primaryMetricBreakdown = _.defaults({
                    dimensions: _.compact(_.union(metric.dimensions, ['companyId'], [metric.exclusionDimension])),
                    companyId: props.focusCompanyId,
                    timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD
                }, props, metric);
                memo[LANDSCAPE_COMPARE_METRIC_LOADS.PrimaryMetricBreakdown] = (memo[LANDSCAPE_COMPARE_METRIC_LOADS.PrimaryMetricBreakdown] || []).concat([primaryMetricBreakdown]);

                memo[LANDSCAPE_COMPARE_METRIC_LOADS.PrimaryMetricBreakdownDeltaRel] = (memo[LANDSCAPE_COMPARE_METRIC_LOADS.PrimaryMetricBreakdownDeltaRel] || []).concat([_.defaults({
                    timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE
                }, primaryMetricBreakdown)]);

                const benchmarkMetric = _.defaults({
                    dimensions: metric.dimensions,
                    timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD,
                    benchmarkCompaniesFilter: selectedBenchmarkCompareAgainst.benchmarkCompaniesFilter,
                    aggregate: selectedBenchmarkCompareAgainst.aggregate
                }, props, metric);
                memo[LANDSCAPE_COMPARE_METRIC_LOADS.BenchmarkMetric] = (memo[LANDSCAPE_COMPARE_METRIC_LOADS.BenchmarkMetric] || []).concat([benchmarkMetric]);

                memo[LANDSCAPE_COMPARE_METRIC_LOADS.BenchmarkMetricDeltaRel] = (memo[LANDSCAPE_COMPARE_METRIC_LOADS.BenchmarkMetricDeltaRel] || []).concat([_.defaults({
                    timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE,
                    benchmarkCompaniesFilter: selectedBenchmarkCompareAgainst.benchmarkCompaniesFilter,
                    aggregate: selectedBenchmarkCompareAgainst.aggregate
                }, props, metric)]);

                let benchmarkDimensions = metric.dimensions;
                const showBenchmarkSubmetrics = shouldShowBenchmarkSubmetrics(selectedBenchmarkCompareAgainst.aggregate, selectedBenchmarkCompareAgainst.benchmarkCompaniesFilter, me);
                if (showBenchmarkSubmetrics) {
                    // If we're showing a median, don't break up the landscape bar into sub-metrics since they might not sum properly.
                    benchmarkDimensions = _.compact(_.union(benchmarkDimensions, [metric.exclusionDimension]));
                }
                const benchmarkMetricBreakdown = _.defaults({
                    dimensions: benchmarkDimensions,
                    timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD,
                    benchmarkCompaniesFilter: selectedBenchmarkCompareAgainst.benchmarkCompaniesFilter,
                    aggregate: selectedBenchmarkCompareAgainst.aggregate
                }, props, metric);
                memo[LANDSCAPE_COMPARE_METRIC_LOADS.BenchmarkMetricBreakdown] = (memo[LANDSCAPE_COMPARE_METRIC_LOADS.BenchmarkMetricBreakdown] || []).concat([benchmarkMetricBreakdown]);

                memo[LANDSCAPE_COMPARE_METRIC_LOADS.BenchmarkMetricBreakdownDeltaRel] = (memo[LANDSCAPE_COMPARE_METRIC_LOADS.BenchmarkMetricBreakdownDeltaRel] || []).concat([_.defaults({
                    timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE
                }, benchmarkMetricBreakdown)]);

                memo[LANDSCAPE_COMPARE_METRIC_LOADS.ObservationMetrics] = (memo[LANDSCAPE_COMPARE_METRIC_LOADS.ObservationMetrics] || []).concat([App.data.getMetricsToLoad(me, metric)]);

                return memo;
            }, {});

            const fetches = _.map(metricLoadTypes, function (metricLoadType) {
                if (metricLoadType === LANDSCAPE_COMPARE_METRIC_LOADS.ObservationMetrics) {
                    return $.when.apply(null, metricCollections[metricLoadType])
                        .then(function () {
                            return Array.prototype.slice.call(arguments);
                        });
                }

                return new RivalIQ.collections.MetricDefinitions(metricCollections[metricLoadType]).fetch();
            });

            return $.when.apply(null, fetches)
                .then(function () {

                    const results = arguments;

                    const resultsByLoadType = _.reduce(metricLoadTypes, function (memo, metricLoadType, idx) {
                        memo[metricLoadType] = results[idx];
                        return memo;
                    }, {});

                    me.collection = new Backbone.Collection(_.times(props.metrics.length, function (idx) {

                        return {
                            primaryMetric: resultsByLoadType[LANDSCAPE_COMPARE_METRIC_LOADS.PrimaryMetric].at(idx),
                            primaryMetricBreakdown: resultsByLoadType[LANDSCAPE_COMPARE_METRIC_LOADS.PrimaryMetricBreakdown].at(idx),
                            primaryMetricDeltaRel: resultsByLoadType[LANDSCAPE_COMPARE_METRIC_LOADS.PrimaryMetricDeltaRel].at(idx),
                            primaryMetricBreakdownDeltaRel: resultsByLoadType[LANDSCAPE_COMPARE_METRIC_LOADS.PrimaryMetricBreakdownDeltaRel].at(idx),

                            benchmarkMetric: resultsByLoadType[LANDSCAPE_COMPARE_METRIC_LOADS.BenchmarkMetric].at(idx),
                            benchmarkMetricBreakdown: resultsByLoadType[LANDSCAPE_COMPARE_METRIC_LOADS.BenchmarkMetricBreakdown].at(idx),
                            benchmarkMetricDeltaRel: resultsByLoadType[LANDSCAPE_COMPARE_METRIC_LOADS.BenchmarkMetricDeltaRel].at(idx),
                            benchmarkMetricBreakdownDeltaRel: resultsByLoadType[LANDSCAPE_COMPARE_METRIC_LOADS.BenchmarkMetricBreakdownDeltaRel].at(idx),

                            observations: App.data.getLandscapeObservations(me, resultsByLoadType[LANDSCAPE_COMPARE_METRIC_LOADS.ObservationMetrics][idx])
                        };
                    }));
                });
        }
    });

    const FooterView = RivalIQ.apps.Dashboard.views.PanelFooter.extend({
        template: '#overviewWidgetFooterTemplate',
        serializeData: function () {
            const me = this;

            return {
                footerText: me.dashboardModel.footerText || '',
                footerUrl: me.dashboardModel.footerUrl || ''
            };
        }
    });

    const LandscapeComparisonWidget = RivalIQ.apps.Dashboard.models.Widget.extend({

        view: LandscapeComparisonContainerView,

        filter: function () {
            const me = this;
            return !RivalIQ.props.channelAccess(me.getProps());
        },

        pdfToPngDpiMultiplier: function () {
            const me = this;

            // We only need to upsample for single channels with an exclusionDimension (a.k.a., graphs that draw a bar pattern).
            const hasExclusionDimension = _.some(me.getProp('metrics'), function (metric) {
                return metric.exclusionDimension;
            });

            return (hasExclusionDimension && me.getProp('channel') ? 4 : 1);
        },

        footerView: FooterView,
        hasFooter: function () {
            const me = this;
            return !me.isExporting();
        },

        processProps: function (props) {
            props.useUniversalMetricsApi = true;

            return props;
        },

        getExportTitle: function () {
            const me = this;

            let title = me.exportTitle || _.result(me, 'title');

            if (!title) {
                const companyNamePossessive = App.utils.makePossessive(_.trim(RivalIQ.state.landscape.getCompanyById(me.getProp('focusCompanyId')).get('name')));
                const channel = me.getProp('channel');
                const channelName = (channel ? RivalIQ.data.channels.get(channel).get('name') : null);
                const metricName = RivalIQ.metric.names.metricName(me.getProp('metrics')[0]);
                title = _.compact([
                    companyNamePossessive,
                    channelName,
                    metricName
                ]).join(' ');
            }

            return title;
        },

        getExportSubtitle: function () {
            const me = this;
            if (me.isFocus()) {
                const timezone = RivalIQ.state.landscape.get('timezone');
                return RivalIQ.utils.timePeriod.getTimePeriodDisplayString(me.getProp('timePeriod'), null, timezone);
            }
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.OVERVIEW_LANDSCAPE_COMPARISON, LandscapeComparisonWidget);
});
